import {
  CheckboxGroup,
  IOption,
  IDropdown,
  IFacet,
  ICheckboxGroup,
  SearchSort,
  Button,
  ButtonStyleType,
  ButtonSizeType,
} from '@into-discovery/browsewebshared';
import { useQueryFacets } from '@into-discovery/browsewebshared/src/hooks/use-query-facets.hook';

import React, { useCallback, useState } from 'react';
import SlideOutPanel from '@/components/molecules/slide-out-panel/slide-out-panel';
import { useFacets } from '@/hooks/use-facet.hook';
import { useRouter } from '@/navigation';
import { useSearchParams } from 'next/navigation';
import colorStyles from '@/styles/utils/variables.module.scss';
import styles from './search-filters-mobile.module.scss';

type Props = {
  filters: ICheckboxGroup[];
  sortOptions: IDropdown[];
  isBreakpointLgUp: boolean;
};

interface FiltersParams {
  toggle: () => void;
  isShowMenu: boolean;
  filters: ICheckboxGroup[];
}

function Filters({ toggle, isShowMenu, filters = [] }: FiltersParams) {
  const [selectedFilters, setSelectedFilters] = useFacets(filters, true);
  const [originFilters, setOriginFilters] = useState([]);
  const searchParams = useSearchParams();
  const queryFacets = useQueryFacets(searchParams);

  const onChangeCheckbox = useCallback(
    (filterOptions: IOption) => {
      const filtersCollection: ICheckboxGroup[] = JSON.parse(
        JSON.stringify(selectedFilters || `[]`),
      );
      filtersCollection.forEach((filterItem: ICheckboxGroup) => {
        const index = filterItem.options.findIndex((x) => x.name === filterOptions.name);
        if (index >= 0) {
          // eslint-disable-next-line no-param-reassign
          filterItem.options[index] = filterOptions;
        }
      });

      setSelectedFilters(filtersCollection);
    },
    [selectedFilters],
  );

  const applySearch = () => {
    const selectedFacets = selectedFilters
      .map((filter): IFacet => {
        const selectedOptions = filter.options
          .filter((option) => option.selected)
          .map((option) => `"${option.id}"`);

        return {
          id: filter.id,
          title: filter.name,
          values: selectedOptions,
        };
      })
      .filter((facet) => facet.values?.length);

    queryFacets.setFacets(selectedFacets);
    toggle();
  };

  const closeFilterPanelHandler = () => {
    setSelectedFilters(originFilters);
    toggle();
  };

  function showFilterPanelHandler() {
    setOriginFilters(selectedFilters);
    toggle();
  }

  return (
    <>
      <button
        type="button"
        value="filter_by"
        name="filter_by"
        id="filter_by"
        title="Filter by"
        className={`${styles[`mobile-filter-toggle`]} text-start position-relative w-100 border-0`}
        onClick={showFilterPanelHandler}
      >
        Filter by
      </button>
      <SlideOutPanel
        isShow={isShowMenu}
        onClose={closeFilterPanelHandler}
        title="Filter by"
        customClass="mobile-filters-panel"
      >
        <div className={`${styles[`mobile-filter-options`]}`}>
          <div>
            {selectedFilters?.map((filter) => (
              <CheckboxGroup
                key={filter.name}
                filter={filter}
                onChangeCheckbox={onChangeCheckbox}
              />
            ))}
          </div>
        </div>
        <div className={`${styles[`mobile-filter-submit`]}`}>
          <Button
            buttonID="apply-mobile-facets"
            buttonAriaLabel="Apply Mobile Facets"
            buttonName="Apply Mobile Facets"
            buttonStyle={ButtonStyleType.Filled}
            buttonSize={ButtonSizeType.Medium}
            copyColor={colorStyles.white}
            backgroundColor={colorStyles.primary}
            hoverBackgroundColor={colorStyles.primaryHover}
            activeBackgroundColor={colorStyles.primaryActive}
            onClick={applySearch}
            buttonRole="button"
          >
            Apply Filters
          </Button>
        </div>
      </SlideOutPanel>
    </>
  );
}

export default function SearchFiltersMobile({ sortOptions, filters, isBreakpointLgUp }: Props) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [isShowMenu, setShowMenu] = useState(false);
  const toggle = () => {
    setShowMenu(!isShowMenu);
  };

  return (
    <div className={`${styles[`mobile-filter-section`]} row g-0`}>
      <div className="col">
        <Filters toggle={toggle} isShowMenu={isShowMenu} filters={filters} />
      </div>
      {sortOptions && (
        <div className="col">
          <SearchSort
            sortOptions={sortOptions}
            router={router}
            searchParams={searchParams}
            isBreakpointLgUp={isBreakpointLgUp}
          />
        </div>
      )}
    </div>
  );
}
